import styled from 'helpers/styles';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import ComponentStyles from 'styles/mixins/Component.styles';
import constants from 'helpers/constants';
import utils from 'helpers/utils';

const StyledButton = styled(Button)
  .attrs((props) => {
    let attr = props;

    const baseColorVars = props.theme.color2Css('Button', null, props.color, {
      varPrefix: 'button',
      variables: true,
      interactive: props.$clickable,
      type: constants.colorTypes.base, // colors all states
    });

    attr = utils.styleProps(attr, baseColorVars.vars, {baseColor: baseColorVars.css});

    const buttonColorVars = props.theme.color2Css('Button', null, props.color, {
      varPrefix: 'button',
      variables: true,
      interactive: props.$clickable,
      type: constants.colorTypes.button
    });

    attr = utils.styleProps(attr, buttonColorVars.vars, {buttonColor: buttonColorVars.css});

    const buttonOutlinedVars = props.theme.color2Css('Button', null, props.color, {
      varPrefix: 'button',
      variables: true,
      interactive: props.$clickable,
      type: constants.colorTypes.outlined
    });

    attr = utils.styleProps(attr, buttonOutlinedVars.vars, {outlinedColor: buttonOutlinedVars.css});

    const vars = {
      '--button-flat-background-color': props.theme.property(`palette.${props.color}.contrastText`, props.theme.property(`palette.secondary.contrastText`))
    };

    return utils.styleProps(attr, vars);
  })`
  
  text-transform: none;
  border-radius: ${props => props.theme.radius(2)};

  &.Button {
    svg {
      font-size: ${props => props.theme.fontSize(16)};
    }
  }
  
  &.Button-disabled {
    svg {
      color: inherit;
    }
  }
  
  // variants
  ${props => props.theme.state2Selector('Button-contained', null, 'hover')} {
    box-shadow: ${props => props.theme.property('shadows.2')};
  }
  
  &.Button-clickable {
    ${props => props.theme.state2Selector('Button-contained', null, ['active', 'focus', 'focusVisible', 'selected'])} {
      box-shadow: ${props => props.theme.property('shadows.3')};
    }
  }

  &.Button-contained {
    box-shadow: unset;
    ${props => props.$css.buttonColor}
  }
  
  &.Button-outlined, &.Button-text {
    ${props => props.$css.outlinedColor}

    ${props => props.theme.state2Selector('Button', null, 'default')} {
      background-color: transparent;
    }
  }
  
  &.Button-outlined {
    &.Button-plainBorder {
      border: 1px solid ${props => props.theme.property('palette.divider')} !important;
    }
    &.Button-flatBackground {
      ${props => props.theme.state2Selector('Button', null, 'default')} {
        background-color: ${props => props.$css.$var('--button-flat-background-color')};
      }
    }
    
    &.Button-grouped:not(:first-of-type) {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      margin-left: -${props => props.theme.layout(1)};
    }

    &.Button-grouped:not(:last-of-type) {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      border-right-color: transparent;
    }
  }
  
  &.Button-flat {
    box-shadow: unset;
  }

  &.Button-flat {
    ${props => props.$css.baseColor}
  }
  
  .Button {
    &-badge {
      .MuiBadge-inline {
        &.MuiBadge-badge {
          position: relative;
          align-self: center;
          padding: ${props => props.theme.layout(5)};
          margin-left: ${props => props.theme.layout('1sp')};
        }
      }
    }
  }
  
  &.Button-size-smallest {
    height: ${props => props.theme.layout(18)};
    min-height: ${props => props.theme.layout(18)};
    font-size: ${props => props.theme.fontSize(12)};
    line-height: ${props => props.theme.lineHeight('1f')};
    border-radius: ${props => props.theme.radius(1)};

    padding: ${props => props.theme.layout(0)} ${props => props.theme.layout(4)};

    svg {
      font-size: ${props => props.theme.fontSize(12)};
    }
    
    .MuiButton-startIcon {
      margin-left: -${props => props.theme.layout(2)};
      margin-right: ${props => props.theme.layout(4)};
    }
    .MuiButton-endIcon {
      margin-left: ${props => props.theme.layout(4)};
      margin-right: -${props => props.theme.layout(2)};
    }
  }
  
  &.Button-size-smaller {
    height: ${props => props.theme.layout(24)};
    min-height: ${props => props.theme.layout(24)};
    font-size: ${props => props.theme.fontSize(13)};
    padding: 0 ${props => props.theme.layout(6)};
    border-radius: ${props => props.theme.radius(1)};
    
    .MuiButton-startIcon {
      margin-left: -${props => props.theme.layout(2)};
      margin-right: ${props => props.theme.layout(4)};
    }
    .MuiButton-endIcon {
      margin-left: ${props => props.theme.layout(4)};
      margin-right: -${props => props.theme.layout(2)};
    }
  }
  
  &.Button-size-small {
    height: ${props => props.theme.layout(32)};
    min-height: ${props => props.theme.layout(32)};
    padding: ${props => props.theme.layout(4)} ${props => props.theme.layout(8)};
    border-radius: ${props => props.theme.radius(1.5)};

    .MuiButton-startIcon {
      margin-left: -${props => props.theme.layout(2)};
      margin-right: ${props => props.theme.layout(6)};
    }
    .MuiButton-endIcon {
      margin-left: ${props => props.theme.layout(4)};
      margin-right: -${props => props.theme.layout(6)};
    }
  }
  
  &.Button-size-medium {
    height: ${props => props.theme.layout(36)};
    min-height: ${props => props.theme.layout(36)};
    padding: ${props => props.theme.layout(5)} ${props => props.theme.layout(12)};
  }
  
  &:not(.Button-clickable) {
    cursor: default;

    .MuiTouchRipple-root {
      display: none;
    }
  }

  &.Button-radius-square {
    border-radius: ${props => props.theme.radius(1)};
  }
  
  ${ComponentStyles}
`;

StyledButton.propTypes = {
  theme: PropTypes.object
};

export default StyledButton;
